import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import logo from '../assets/LinkedIn-Local-Cameroon-Logo.png';

const TicketCard = ({ ticketData }) => {
  const ticketRef = useRef(null);

  const {
    name = 'Attendee Name',
    email = '',
    phone = '',
    ticketType = 'standard',
    ticketId = 'LLC-2026-XXXX',
    photo = null,
  } = ticketData || {};

  const getTierDetails = () => {
    switch (ticketType) {
      case 'vip':
        return { label: 'VIP PASS', price: '30,000 FCFA', color: '#c1001f' };
      case 'standard':
        return { label: 'STANDARD PASS', price: '15,000 FCFA', color: '#0077b5' };
      case 'earlybird':
      default:
        return { label: 'EARLY BIRD PASS', price: '10,000 FCFA', color: '#007a33' };
    }
  };
  const { label: tierLabel, price: tierPrice, color: tierColor } = getTierDetails();

  const handleDownloadPNG = async () => {
    if (!ticketRef.current) return;
    try {
      const canvas = await html2canvas(ticketRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `LLC2026-Ticket-${ticketId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('PNG export failed:', err);
    }
  };

  const handleDownloadPDF = async () => {
    if (!ticketRef.current) return;
    try {
      const canvas = await html2canvas(ticketRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // A4-ish landscape oriented to the ticket shape
      const pdfWidth = 280;
      const pdfHeight = (imgHeight * pdfWidth) / imgWidth;
      
      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pdfWidth + 20, pdfHeight + 20],
      });
      
      pdf.addImage(imgData, 'PNG', 10, 10, pdfWidth, pdfHeight);
      pdf.save(`LLC2026-Ticket-${ticketId}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  };

  return (
    <div className="ticket-export-wrapper">
      {/* ===== THE TICKET (captured by html2canvas) ===== */}
      <div className="premium-ticket" ref={ticketRef}>
        {/* Decorative top band */}
        <div className="premium-ticket__band" style={{ background: tierColor }}></div>

        <div className="premium-ticket__body">
          {/* LEFT: Main ticket area */}
          <div className="premium-ticket__main">
            {/* Header row: logo + event info */}
            <div className="premium-ticket__header">
              <img src={logo} alt="LLC" className="premium-ticket__logo" />
              <div className="premium-ticket__event-info">
                <div className="premium-ticket__event-name">LINKEDIN LOCAL CAMEROON 2026</div>
                <div className="premium-ticket__event-tagline">Rise Locally, Lead Globally</div>
              </div>
            </div>

            {/* Attendee section */}
            <div className="premium-ticket__attendee-row">
              {/* Photo */}
              <div className="premium-ticket__photo-frame" style={{ borderColor: tierColor }}>
                {photo ? (
                  <img src={photo} alt={name} className="premium-ticket__photo" />
                ) : (
                  <div className="premium-ticket__photo-placeholder">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="premium-ticket__details">
                <div className="premium-ticket__detail-group">
                  <span className="premium-ticket__label">ATTENDEE NAME</span>
                  <span className="premium-ticket__value premium-ticket__value--name">{name.toUpperCase()}</span>
                </div>
                <div className="premium-ticket__detail-row">
                  <div className="premium-ticket__detail-group">
                    <span className="premium-ticket__label">TICKET TYPE</span>
                    <span className="premium-ticket__value" style={{ color: tierColor, fontWeight: 800 }}>{tierLabel}</span>
                  </div>
                  <div className="premium-ticket__detail-group">
                    <span className="premium-ticket__label">AMOUNT</span>
                    <span className="premium-ticket__value">{tierPrice}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom info strip */}
            <div className="premium-ticket__info-strip">
              <div className="premium-ticket__info-item">
                <span className="premium-ticket__label">DATE</span>
                <span className="premium-ticket__value">10 OCTOBER 2026</span>
              </div>
              <div className="premium-ticket__info-item">
                <span className="premium-ticket__label">VENUE</span>
                <span className="premium-ticket__value">BEST WESTERN PLUS HOTEL</span>
              </div>
              <div className="premium-ticket__info-item">
                <span className="premium-ticket__label">TIME</span>
                <span className="premium-ticket__value">7:00 AM - 8:00 PM</span>
              </div>
              <div className="premium-ticket__info-item">
                <span className="premium-ticket__label">CITY</span>
                <span className="premium-ticket__value">DOUALA, CAMEROON</span>
              </div>
            </div>
          </div>

          {/* PERFORATION LINE */}
          <div className="premium-ticket__perforation"></div>

          {/* RIGHT: QR Stub */}
          <div className="premium-ticket__stub">
            <QRCodeSVG
              value={`https://linkedinlocalcameroon.com/verify/${ticketId}`}
              size={110}
              level="M"
              bgColor="transparent"
              fgColor="#1a1a2e"
              style={{ marginBottom: 12 }}
            />
            <div className="premium-ticket__ticket-id">{ticketId}</div>
            <div className="premium-ticket__stub-label">SCAN TO VERIFY</div>
          </div>
        </div>

        {/* Bottom band */}
        <div className="premium-ticket__band premium-ticket__band--bottom" style={{ background: tierColor }}></div>
      </div>

      {/* ===== EXPORT BUTTONS ===== */}
      <div className="ticket-export-actions">
        <button className="btn btn-blue ticket-export-btn" onClick={handleDownloadPNG}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 8}}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download PNG
        </button>
        <button className="btn btn-red ticket-export-btn" onClick={handleDownloadPDF}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 8}}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
          </svg>
          Download PDF
        </button>
      </div>
    </div>
  );
};

export default TicketCard;
